import { defer } from 'lodash-es'
import { Controller } from 'stimulus'
import { toLuxonDateTime } from '@fullcalendar/luxon2'
import { syncDatePickerAndFullCalendar } from './booking/view_syncer'
import { BookingFormManager } from './booking/booking_form_manager'
import { DesktopBookingFlowManager } from './booking/desktop_booking_flow'
import { MobileBookingFlowManager } from './booking/mobile_booking_flow'
import { getSlotsForRange } from './booking/slot_manager'
import { toLuxonDateTimeKeepingSameTime } from './booking/helpers'
import { DatePickerRestrictionsManager } from './booking/date_picker_restriction_manager'
import { syncTimeZoneSelects } from './booking/time_zone_syncer'

/*
 * We've got 2 very different booking flows that are being handled by this controller
 * 1. On desktop, we use fullcalendar to display a calendar. People hover over an available slot on the calendar which shows a popup with a booking form.
 * 2. On mobile, we use a date picker to first select a date. Then we show available slots for that date. Then the same booking form where people can confirm booking by entering their name/email/details.
 *
 * On desktop, we also show a datepicker (same one we use on mobile), but it acts like a navigator for the bigger calendar (fullcalendar).
 * The fullcalendar calendar is available on the `calendar` outlet and the date picker is available on the `dateTimePicker` outlet.
 *
 * Both flows render a timezone select (TimeZoneSelectComponent with hidden field): the week calendar header on desktop, a pill above the
 * date picker on phones, and the slot picker's in the desktop monthly view. We keep their hidden fields in sync via time_zone_syncer.
 */

export default class extends Controller {
  static targets = [
    // Shown on mobile & desktop view
    'bookingForm',

    // Time zone selects outside the week calendar header (phone pill, desktop monthly slot picker)
    'mobileTimeZoneSelect',

    // Show only on mobile view
    'slotPicker',
    'slotPickerDate',
    'slotPickerButtonGroup',
    'slotPickerEmptyState',

    // No availability state
    'noAvailabilityMessage',
    'datePickerArea',
    'weekCalendarArea',
  ]
  static outlets = ['calendar', 'date-time-picker']
  static values = {
    durationInMinutes: Number,
    schedulingInterval: Number,
    locale: String,
  }

  calendarOutletConnected() {
    this.calendarConnected = true
    defer(this.initialize)
  }

  dateTimePickerOutletConnected() {
    this.dateTimePickerConnected = true
    defer(this.initialize)
  }

  disconnect() {
    if (this.teardownTimezoneSync) this.teardownTimezoneSync()
    this.calendarOutlet?.element?.removeEventListener(
      'calendar:timeZoneChanged',
      this.timeZoneChanged
    )
    // Tear down the booking-flow managers so their document listeners don't
    // survive the controller and react to later events from detached DOM.
    document.removeEventListener('capacity-hold-slot:taken', this.onSlotTaken)
    this.bookingFormManager?.destroy?.()
    this.desktopBookingFlowManager?.destroy?.()
    this.mobileBookingFlowManager?.destroy?.()
  }

  initialize = () => {
    // https://github.com/hotwired/stimulus/issues/618
    const outletsConnected =
      this.calendarConnected && this.dateTimePickerConnected
    if (!outletsConnected || this.initialized) return
    this.initialized = true
    this.fullcalendar = this.calendarOutlet.calendar

    this.getSlots = (...args) =>
      getSlotsForRange(
        ...args,
        this.durationInMinutesValue,
        this.calendarOutlet.eventStore.fetchEvents,
        this.schedulingIntervalValue || 15
      )

    this.calendarOutlet.element.addEventListener(
      'calendar:timeZoneChanged',
      this.timeZoneChanged
    )

    this.bookingFormManager = new BookingFormManager(
      this.bookingFormTarget,
      this.toLuxonDateTimeWithTz,
      this.localeValue
    )

    this.desktopBookingFlowManager = new DesktopBookingFlowManager(
      this.fullcalendar,
      this.durationInMinutesValue,
      this.bookingFormManager.show
    )

    this.mobileBookingFlowManager = new MobileBookingFlowManager({
      locale: this.localeValue,
      datePickerController: this.dateTimePickerOutlet,
      durationInMinutesValue: this.durationInMinutesValue,
      showBookingForm: this.bookingFormManager.show,
      hideBookingForm: this.bookingFormManager.hide,
      getSlots: this.getSlots,
      toLuxonDateTime: this.toLuxonDateTimeWithTz,
      toLuxonDateTimeKeepingSameTime: this.toLuxonDateTimeWithTzKeepingSameTime,
      onTimeZoneChange: this.onTimeZoneChange,
      slotPickerElements: {
        container: this.slotPickerTarget,
        buttonGroup: this.slotPickerButtonGroupTarget,
        date: this.slotPickerDateTarget,
        emptyState: this.slotPickerEmptyStateTarget,
      },
    })

    document.addEventListener('capacity-hold-slot:taken', this.onSlotTaken)

    this.datePickerRestrictionsManager = new DatePickerRestrictionsManager(
      this.dateTimePickerOutlet,
      this.getSlots,
      this.onTimeZoneChange,
      this.toLuxonDateTimeWithTzKeepingSameTime
    )

    if (
      this.hasMobileTimeZoneSelectTarget &&
      this.calendarOutlet.hasTimeZoneSelectTarget
    ) {
      this.teardownTimezoneSync = syncTimeZoneSelects([
        this.calendarOutlet.timeZoneSelectTarget,
        ...this.mobileTimeZoneSelectTargets,
      ])
    }
    syncDatePickerAndFullCalendar(this.fullcalendar, this.dateTimePickerOutlet)

    const initialTz = this.hasMobileTimeZoneSelectTarget
      ? this.mobileTimeZoneSelectTarget.value
      : this.calendarOutlet.hasTimeZoneSelectTarget
      ? this.calendarOutlet.timeZoneSelectTarget.value
      : null
    if (initialTz != null) this.setParticipantTimeZone(initialTz)

    this.onTimeZoneChange((iana) => this.setParticipantTimeZone(iana))
  }

  // Someone else just took the time this visitor picked (capacity_hold_slot_controller.js): close
  // the form and fetch the free times again.
  onSlotTaken = () => {
    this.bookingFormManager.hide()
    this.calendarOutlet.refresh() // clears the cached availability, then refetches
    this.mobileBookingFlowManager.updateSlotPicker()
  }

  toLuxonDateTimeWithTz = (date) => toLuxonDateTime(date, this.fullcalendar)

  toLuxonDateTimeWithTzKeepingSameTime = (date) =>
    toLuxonDateTimeKeepingSameTime(date, this.fullcalendar)

  onTimeZoneChange = (cb) => {
    this.onTimeZoneChangeCallbacks = this.onTimeZoneChangeCallbacks || []
    this.onTimeZoneChangeCallbacks.push(cb)
  }
  timeZoneChanged = (e) => {
    ;(this.onTimeZoneChangeCallbacks || []).forEach((cb) =>
      cb(e.detail.timeZone)
    )
  }

  // Keep the page and the calendar visible so the visitor still sees what they tried to
  // book; just lock the calendar areas and reveal the overlay inside whichever is shown.
  showNoAvailability = () => {
    this.datePickerAreaTargets
      .concat(this.weekCalendarAreaTargets)
      .forEach((el) => {
        el.classList.add('pointer-events-none')
        // inert: no focus, no keyboard navigation either. The message is a sibling, so it stays readable.
        el.querySelectorAll(
          '.calendar-component, .date-time-picker-component'
        ).forEach((calendar) => (calendar.inert = true))
      })
    this.noAvailabilityMessageTargets.forEach((el) =>
      el.classList.remove('hidden')
    )
  }

  hideBookingForm = () => {
    this.bookingFormManager.hide()
  }

  // Links with several durations let the person booking pick one; slots are rebuilt for the new length.
  selectDuration(event) {
    const minutes = Number(event.target.value)
    if (minutes > 0) this.durationInMinutesValue = minutes
  }

  durationInMinutesValueChanged(minutes) {
    if (!this.initialized) return

    // Hiding the form also clears the desktop active slot (booking-form:hide)
    this.bookingFormManager.hide()
    this.desktopBookingFlowManager.setDuration(minutes)
    this.mobileBookingFlowManager.setDuration(minutes)
    this.datePickerRestrictionsManager.updateRestrictions(
      this.dateTimePickerOutlet.getViewDate()
    )
  }

  hideSlotPicker = () => {
    this.mobileBookingFlowManager.hideSlotPicker()
  }

  setParticipantTimeZone = (iana) => {
    const el = document.querySelector('.booking-time-zone')
    if (el) el.value = iana
  }
}
